import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  CheckSquare,
  Square,
  Plus,
  ChevronRight,
  Scale,
  FileSignature,
  CreditCard,
  Send,
  FileCheck,
  AlertCircle,
  DollarSign,
  Sparkles,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

// Default contract phases
const DEFAULT_PHASES = [
  { id: "drafting", name: "Contract Drafting", description: "Create and prepare contract documents", order: 1 },
  { id: "internal_review", name: "Internal Review", description: "Legal and management approval", order: 2 },
  { id: "sent", name: "Sent to Prospect", description: "Contract delivered to customer", order: 3 },
  { id: "negotiation", name: "Contract Negotiation", description: "Handle redlines and amendments", order: 4 },
  { id: "signature", name: "Signature Collection", description: "Get all required signatures", order: 5 },
  { id: "payment", name: "Payment Setup", description: "Process initial payment", order: 6 },
];

// Default tasks per phase
const DEFAULT_TASKS = {
  drafting: [
    { name: "Select contract template", priority: "high" },
    { name: "Customize terms based on proposal", priority: "high" },
    { name: "Add pricing and payment terms", priority: "high" },
    { name: "Include SLA and support terms", priority: "medium" },
    { name: "Review for completeness", priority: "medium" },
  ],
  internal_review: [
    { name: "Submit to legal review", priority: "high" },
    { name: "Get pricing approval", priority: "high" },
    { name: "Management sign-off", priority: "high" },
    { name: "Compliance check", priority: "medium" },
    { name: "Final internal approval", priority: "high" },
  ],
  sent: [
    { name: "Prepare cover letter", priority: "medium" },
    { name: "Send contract via DocuSign/email", priority: "high" },
    { name: "Confirm receipt", priority: "high" },
    { name: "Schedule review call", priority: "medium" },
    { name: "Set follow-up reminder", priority: "low" },
  ],
  negotiation: [
    { name: "Review prospect redlines", priority: "high" },
    { name: "Consult legal on changes", priority: "high" },
    { name: "Prepare counter-proposal", priority: "medium" },
    { name: "Negotiate final terms", priority: "high" },
    { name: "Get final agreement", priority: "high" },
  ],
  signature: [
    { name: "Send final version for signature", priority: "high" },
    { name: "Get customer signature", priority: "high" },
    { name: "Get internal signature", priority: "high" },
    { name: "Countersign if needed", priority: "medium" },
    { name: "Archive signed contract", priority: "medium" },
  ],
  payment: [
    { name: "Send invoice", priority: "high" },
    { name: "Set up payment method", priority: "high" },
    { name: "Process initial payment", priority: "high" },
    { name: "Confirm payment received", priority: "high" },
    { name: "Handoff to implementation", priority: "medium" },
  ],
};

export default function ContractsWorkflowPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  
  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
        
        if (data.contracts_workflow) {
          setWorkflow(data.contracts_workflow);
          const firstIncomplete = data.contracts_workflow.phases?.find(p => p.status !== "completed");
          setActivePhase(firstIncomplete?.id || data.contracts_workflow.phases?.[0]?.id);
        } else {
          initializeWorkflow(data);
        }
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const initializeWorkflow = (leadData) => {
    const phases = DEFAULT_PHASES.map(phase => ({
      ...phase,
      status: phase.order === 1 ? "in_progress" : "not_started",
      started_at: phase.order === 1 ? new Date().toISOString() : null,
      completed_at: null,
      tasks: (DEFAULT_TASKS[phase.id] || []).map((task, idx) => ({
        id: `task_${phase.id}_${idx}`,
        ...task,
        completed: false,
        completed_at: null,
        completed_by: null,
      }))
    }));

    const wf = {
      started_at: new Date().toISOString(),
      contract_value: leadData?.budget || null,
      contract_status: "draft",
      expected_signature_date: null,
      phases,
      health: "on_track",
    };
    
    setWorkflow(wf);
    setActivePhase("drafting");
    saveWorkflow(wf);
  };

  const saveWorkflow = async (wfData) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/contracts-workflow`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ contracts_workflow: wfData })
      });
      
      if (response.ok) {
        toast.success("Progress saved");
      }
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setSaving(false);
    }
  };

  const toggleTask = async (phaseId, taskId) => {
    const updatedWf = { ...workflow };
    const phase = updatedWf.phases.find(p => p.id === phaseId);
    const task = phase?.tasks.find(t => t.id === taskId);
    
    if (task) {
      task.completed = !task.completed;
      task.completed_at = task.completed ? new Date().toISOString() : null;
      task.completed_by = task.completed ? localStorage.getItem("admin_email") : null;
      
      const allComplete = phase.tasks.every(t => t.completed);
      if (allComplete && phase.status !== "completed") {
        phase.status = "completed";
        phase.completed_at = new Date().toISOString();
        
        // Update contract status based on phase
        const statusMap = {
          "drafting": "draft",
          "internal_review": "under_review",
          "sent": "sent",
          "negotiation": "negotiating",
          "signature": "pending_signature",
          "payment": "signed"
        };
        updatedWf.contract_status = statusMap[phase.id] || updatedWf.contract_status;
        
        const nextPhase = updatedWf.phases.find(p => p.order === phase.order + 1);
        if (nextPhase && nextPhase.status === "not_started") {
          nextPhase.status = "in_progress";
          nextPhase.started_at = new Date().toISOString();
          setActivePhase(nextPhase.id);
        }
        
        toast.success(`${phase.name} phase completed!`);
      }
      
      setWorkflow(updatedWf);
      saveWorkflow(updatedWf);
    }
  };

  const updatePhaseStatus = (phaseId, status) => {
    const updatedWf = { ...workflow };
    const phase = updatedWf.phases.find(p => p.id === phaseId);
    
    if (phase) {
      phase.status = status;
      if (status === "in_progress" && !phase.started_at) {
        phase.started_at = new Date().toISOString();
      }
      if (status === "completed") {
        phase.completed_at = new Date().toISOString();
      }
      
      setWorkflow(updatedWf);
      saveWorkflow(updatedWf);
    }
  };

  const addTask = (phaseId) => {
    if (!newTaskName.trim()) return;
    
    const updatedWf = { ...workflow };
    const phase = updatedWf.phases.find(p => p.id === phaseId);
    
    if (phase) {
      phase.tasks.push({
        id: `task_${phaseId}_${Date.now()}`,
        name: newTaskName.trim(),
        priority: newTaskPriority,
        completed: false,
      });
      
      setWorkflow(updatedWf);
      saveWorkflow(updatedWf);
      setNewTaskName("");
      setShowAddTask(false);
      toast.success("Task added");
    }
  };

  const updateHealth = (health) => {
    const updatedWf = { ...workflow, health };
    setWorkflow(updatedWf);
    saveWorkflow(updatedWf);
  };

  const getProgressPercentage = () => {
    if (!workflow?.phases) return 0;
    const totalTasks = workflow.phases.reduce((sum, p) => sum + p.tasks.length, 0);
    const completedTasks = workflow.phases.reduce((sum, p) => sum + p.tasks.filter(t => t.completed).length, 0);
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const getPhaseProgress = (phase) => {
    const total = phase.tasks.length;
    const completed = phase.tasks.filter(t => t.completed).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not set";
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getContractStatusBadge = (status) => {
    const statusConfig = {
      "draft": { label: "Draft", color: "bg-zinc-500/20 text-zinc-400" },
      "under_review": { label: "Under Review", color: "bg-yellow-500/20 text-yellow-400" },
      "sent": { label: "Sent", color: "bg-blue-500/20 text-blue-400" },
      "negotiating": { label: "Negotiating", color: "bg-orange-500/20 text-orange-400" },
      "pending_signature": { label: "Pending Signature", color: "bg-purple-500/20 text-purple-400" },
      "signed": { label: "Signed", color: "bg-green-500/20 text-green-400" },
    };
    return statusConfig[status] || statusConfig["draft"];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  const currentPhase = workflow?.phases?.find(p => p.id === activePhase);
  const progress = getProgressPercentage();
  const contractStatus = getContractStatusBadge(workflow?.contract_status);

  return (
    <div className="min-h-screen bg-[#050505] p-6" data-testid="contracts-workflow-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate(`/admin/leads/${leadId}`)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {lead?.first_name}'s Profile
            </button>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500">
                <FileSignature className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Contracts Workflow</h1>
                <p className="text-zinc-400">{lead?.company_name || lead?.first_name + " " + lead?.last_name}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 rounded-lg text-sm font-medium ${contractStatus.color}`}>
              {contractStatus.label}
            </span>
            <select
              value={workflow?.health || "on_track"}
              onChange={(e) => updateHealth(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm"
            >
              <option value="on_track">🟢 On Track</option>
              <option value="at_risk">🟡 At Risk</option>
              <option value="blocked">🔴 Blocked</option>
            </select>
            {saving && <Loader2 className="w-4 h-4 animate-spin text-orange-400" />}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-4 space-y-6">
            {/* Contract Details */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Contract Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Contract Value</label>
                  <input
                    type="text"
                    value={workflow?.contract_value || ""}
                    onChange={(e) => {
                      const updatedWf = { ...workflow, contract_value: e.target.value };
                      setWorkflow(updatedWf);
                      saveWorkflow(updatedWf);
                    }}
                    placeholder="e.g., $50,000"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Expected Signature Date</label>
                  <input
                    type="date"
                    value={workflow?.expected_signature_date || ""}
                    onChange={(e) => {
                      const updatedWf = { ...workflow, expected_signature_date: e.target.value };
                      setWorkflow(updatedWf);
                      saveWorkflow(updatedWf);
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Started</label>
                  <p className="text-white text-sm">{formatDate(workflow?.started_at)}</p>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Progress</h3>
              
              <div className="relative w-28 h-28 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="56" cy="56" r="48" fill="none" stroke="#27272a" strokeWidth="10" />
                  <circle
                    cx="56" cy="56" r="48" fill="none"
                    stroke="url(#orangeGradient)" strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={`${progress * 3.02} 302`}
                  />
                  <defs>
                    <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#f97316" />
                      <stop offset="100%" stopColor="#ef4444" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{progress}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xl font-bold text-white">{workflow?.phases?.filter(p => p.status === "completed").length || 0}</p>
                  <p className="text-[10px] text-zinc-500">Done</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xl font-bold text-white">{workflow?.phases?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500">Phases</p>
                </div>
              </div>
            </div>

            {/* Phase List */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Phases</h3>
              
              <div className="space-y-2">
                {workflow?.phases?.map((phase) => (
                  <button
                    key={phase.id}
                    onClick={() => setActivePhase(phase.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                      activePhase === phase.id
                        ? "bg-orange-500/20 border border-orange-500/50"
                        : "bg-zinc-800/50 border border-transparent hover:border-zinc-700"
                    }`}
                  >
                    {phase.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    ) : phase.status === "in_progress" ? (
                      <Clock className="w-5 h-5 text-orange-400" />
                    ) : (
                      <Circle className="w-5 h-5 text-zinc-600" />
                    )}
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-medium ${activePhase === phase.id ? "text-orange-300" : "text-white"}`}>
                        {phase.name}
                      </p>
                      <p className="text-xs text-zinc-500">{getPhaseProgress(phase)}%</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${activePhase === phase.id ? "text-orange-400" : "text-zinc-600"}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Active Phase */}
          <div className="col-span-8">
            {currentPhase && (
              <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold text-white">{currentPhase.name}</h2>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        currentPhase.status === "completed" ? "bg-green-500/20 text-green-400" :
                        currentPhase.status === "in_progress" ? "bg-orange-500/20 text-orange-400" :
                        "bg-zinc-700/50 text-zinc-400"
                      }`}>
                        {currentPhase.status === "completed" ? "Completed" :
                         currentPhase.status === "in_progress" ? "In Progress" : "Not Started"}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">{currentPhase.description}</p>
                  </div>
                  
                  {currentPhase.status !== "completed" && (
                    <select
                      value={currentPhase.status}
                      onChange={(e) => updatePhaseStatus(currentPhase.id, e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Mark Complete</option>
                    </select>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Phase Progress</span>
                    <span className="text-sm font-medium text-white">{getPhaseProgress(currentPhase)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-300"
                      style={{ width: `${getPhaseProgress(currentPhase)}%` }}
                    />
                  </div>
                </div>

                {/* Tasks */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">Tasks</h3>
                    <Button
                      onClick={() => setShowAddTask(!showAddTask)}
                      variant="ghost"
                      size="sm"
                      className="text-orange-400 hover:text-orange-300"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Task
                    </Button>
                  </div>

                  {showAddTask && (
                    <div className="flex items-center gap-3 mb-4 p-4 bg-zinc-800/50 rounded-xl">
                      <input
                        type="text"
                        value={newTaskName}
                        onChange={(e) => setNewTaskName(e.target.value)}
                        placeholder="Task name..."
                        className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      />
                      <select
                        value={newTaskPriority}
                        onChange={(e) => setNewTaskPriority(e.target.value)}
                        className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <Button onClick={() => addTask(currentPhase.id)} size="sm" className="bg-orange-500 hover:bg-orange-600">
                        Add
                      </Button>
                    </div>
                  )}

                  <div className="space-y-2">
                    {currentPhase.tasks.map((task) => (
                      <div
                        key={task.id}
                        className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                          task.completed
                            ? "bg-green-500/10 border border-green-500/20"
                            : "bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600"
                        }`}
                      >
                        <button onClick={() => toggleTask(currentPhase.id, task.id)} className="flex-shrink-0">
                          {task.completed ? (
                            <CheckSquare className="w-5 h-5 text-green-400" />
                          ) : (
                            <Square className="w-5 h-5 text-zinc-500 hover:text-orange-400" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <p className={`text-sm ${task.completed ? "text-zinc-400 line-through" : "text-white"}`}>
                            {task.name}
                          </p>
                          {task.completed && task.completed_by && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Completed by {task.completed_by} on {formatDate(task.completed_at)}
                            </p>
                          )}
                        </div>
                        
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          task.priority === "high" ? "bg-red-500/20 text-red-400" :
                          task.priority === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-zinc-700/50 text-zinc-400"
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ZOEY AI Actions - Separate Card */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h3 className="font-medium text-white">ZOEY AI Actions</h3>
                </div>
                <span className="text-xs text-zinc-500">Click to open action panel</span>
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-call`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                    <Phone className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Call</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-email`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-blue-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Email</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-text`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-purple-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Text</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-appointment`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-orange-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <Calendar className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-sm text-zinc-300">Schedule</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
